import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMuseum } from '@/features/museum/context/MuseumContext';
import { useNotification } from '@/shared/context/NotificationContext';
import { getFirebaseDb } from '@/shared/services/firebase/config';
import { doc, getDoc } from 'firebase/firestore';
import { FIRESTORE_COLLECTIONS, ContentLanguageCode } from '@/shared/constants';
import { buildExhibitionColorMap, buildExhibitionColorOptions } from '@/features/exhibitions/utils/exhibitionColors';
import { logger } from '@/shared/utils/logger';
import {
  getContentLanguageKeys,
  getMuseumLanguagesAsContentCodes,
  sortLanguagesWithFrFirst,
} from '@/shared/utils/languageUtils';
import { handleFirebaseError } from '@/shared/services/firebase/errors';
import { Exhibition, Work } from '@/shared/types';
import { useOrderManager } from '@/shared/hooks/useOrderManager';
import { reorganizeAfterDrag } from '@/shared/utils/orderManager';
import { useExhibitions } from '@/features/exhibitions/hooks/useExhibitions';
import { useWorks } from '@/features/exhibitions/hooks/useWorks';
import { saveExhibition, type ExhibitionTranslations } from '../utils/saveExhibition';
import { findLanguagesMissingImageAlt } from '../utils/imageAlt';

export const useExhibitionDetail = () => {
  const { t } = useTranslation();
  const { showError } = useNotification();
  const { museum, museumSlug, loading: museumLoading } = useMuseum();
  const navigate = useNavigate();
  const { exhibitionId, museumSlug: museumSlugParam } = useParams<{
    exhibitionId: string;
    museumSlug?: string;
  }>();

  const [exhibition, setExhibition] = useState<Exhibition | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [episodeFilter, setEpisodeFilter] = useState<'all' | 'published' | 'drafts'>('all');

  const [statusByLanguage, setStatusByLanguage] = useState<
    Record<ContentLanguageCode, 'draft' | 'published'>
  >({ fr: 'draft', en: 'draft' });
  const [color, setColor] = useState<string>('default');
  const [order, setOrder] = useState<number>(1);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [coverRemoved, setCoverRemoved] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [colorDropdownOpen, setColorDropdownOpen] = useState(false);
  const [orderDropdownOpen, setOrderDropdownOpen] = useState(false);
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<ContentLanguageCode>('en');
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [createWorkModalOpen, setCreateWorkModalOpen] = useState(false);
  const [editingWork, setEditingWork] = useState<Work | null>(null);
  const [draggedWorkId, setDraggedWorkId] = useState<string | null>(null);
  const [dragOverWorkId, setDragOverWorkId] = useState<string | null>(null);
  const [dragOverPlace, setDragOverPlace] = useState<'before' | 'after'>('before');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [activeLanguages, setActiveLanguages] = useState<ContentLanguageCode[]>(['fr']);
  const [currentLanguage, setCurrentLanguage] = useState<ContentLanguageCode>('fr');
  const [translations, setTranslations] = useState<ExhibitionTranslations>({
    fr: { title: '', description: '' },
    en: { title: '', description: '' },
  });

  const museumLanguages = getMuseumLanguagesAsContentCodes(museum);
  const exhibitionColorOptions = buildExhibitionColorOptions(museum?.availableExhibitionColors);
  const effectiveAvailableColors = exhibitionColorOptions.map((option) => option.key);
  const exhibitionColorMap = buildExhibitionColorMap(exhibitionColorOptions);

  const {
    exhibitions: allExhibitions,
    deleteExhibition,
    refetch: refetchExhibitions,
  } = useExhibitions();

  const {
    works: fetchedWorks,
    loading: loadingWorks,
    error: worksError,
    refetch: refetchWorks,
  } = useWorks(exhibitionId);

  const [works, setWorks] = useState<Work[]>([]);

  useEffect(() => {
    setWorks(fetchedWorks);
  }, [fetchedWorks]);

  const hydrateExhibition = (exhibitionData: Exhibition) => {
    setExhibition(exhibitionData);
    setCoverRemoved(false);
    setOrder(exhibitionData.order || 1);
    if (exhibitionData.color) {
      setColor(exhibitionData.color);
    }

    const languages = getContentLanguageKeys(
      exhibitionData.name,
      exhibitionData.description,
      exhibitionData.status
    );
    if (languages.length > 0) {
      const sortedLanguages = sortLanguagesWithFrFirst(languages);
      setActiveLanguages(sortedLanguages);
      setCurrentLanguage((prev) => (sortedLanguages.includes(prev) ? prev : sortedLanguages[0]));

      const translationsData = {} as ExhibitionTranslations;
      const statusData = {} as Record<ContentLanguageCode, 'draft' | 'published'>;

      languages.forEach((lang) => {
        translationsData[lang] = {
          title: exhibitionData.name?.[lang] || '',
          description: exhibitionData.description?.[lang] || '',
          coverAlt: exhibitionData.coverAlt?.[lang] || '',
        };
        statusData[lang] = exhibitionData.status?.[lang] || 'draft';
      });
      setTranslations(translationsData);
      setStatusByLanguage(statusData);
    }
  };

  const loadExhibition = async () => {
    if (!museum?.id || !exhibitionId) {
      return;
    }

    const db = getFirebaseDb();
    if (!db) {
      setLoadError(t('exhibitions.databaseError'));
      setLoading(false);
      return;
    }

    try {
      setLoadError(null);
      const exhibitionRef = doc(
        db,
        FIRESTORE_COLLECTIONS.MUSEUMS,
        museum.id,
        FIRESTORE_COLLECTIONS.EXHIBITIONS,
        exhibitionId
      );
      const exhibitionDoc = await getDoc(exhibitionRef);

      if (!exhibitionDoc.exists()) {
        setExhibition(null);
        setLoadError(t('exhibitions.exhibitionNotFound'));
        return;
      }

      const exhibitionData = {
        id: exhibitionDoc.id,
        ...exhibitionDoc.data(),
      } as Exhibition;

      hydrateExhibition(exhibitionData);

      if (exhibitionData.cover) {
        try {
          const { getFirebaseImageUrl } = await import('@/shared/utils/imageUtils');
          const imageUrl = await getFirebaseImageUrl(exhibitionData.cover);
          setImagePreview(imageUrl);
        } catch (error) {
          logger.error("Erreur lors du chargement de l'image:", error);
        }
      } else {
        setImagePreview(null);
      }
    } catch (error) {
      logger.error("Erreur lors de la récupération de l'exposition:", error);
      setLoadError(t('common.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const { changeOrder: changeExhibitionOrder } = useOrderManager<Exhibition>({
    museumId: museum?.id || '',
    collectionPath: [FIRESTORE_COLLECTIONS.EXHIBITIONS],
    onSuccess: () => {
      refetchExhibitions();
      loadExhibition();
    },
    onError: (error) => {
      showError(error);
    },
  });

  const { reorderAfterDrag: reorderWorks } = useOrderManager<Work>({
    museumId: museum?.id || '',
    collectionPath: [
      FIRESTORE_COLLECTIONS.EXHIBITIONS,
      exhibitionId || '',
      FIRESTORE_COLLECTIONS.WORKS,
    ],
    onSuccess: () => {
      refetchWorks();
    },
    onError: (error) => {
      showError(error);
    },
  });

  useEffect(() => {
    if (museumLoading) {
      return;
    }
    if (!museum?.id || !exhibitionId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    loadExhibition();
    // Intentionally omit loadExhibition: recharge uniquement quand le musée ou l'id change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [museum?.id, exhibitionId, museumLoading]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.dropdown')) {
        setStatusDropdownOpen(false);
        setColorDropdownOpen(false);
        setOrderDropdownOpen(false);
      }
      if (!target.closest('.language-dropdown')) {
        setLanguageDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setCoverRemoved(false);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDelete = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setCoverRemoved(true);
  };

  const handleSaveDraft = async () => {
    if (!museum?.id || !exhibitionId) {
      showError(t('exhibitions.exhibitionNotFound'));
      return;
    }

    const missingAlt = findLanguagesMissingImageAlt({
      languages: activeLanguages,
      hasImage: Boolean(imagePreview),
      imageAlt: Object.fromEntries(activeLanguages.map((lang) => [lang, translations[lang]?.coverAlt || ''])),
      status: statusByLanguage,
    });
    if (missingAlt.length > 0) {
      showError(
        t('exhibitions.coverAlt.missing', { languages: missingAlt.map((lang) => lang.toUpperCase()).join(', ') })
      );
      return;
    }

    setSaving(true);
    try {
      const result = await saveExhibition({
        museumId: museum.id,
        exhibitionId,
        exhibition,
        selectedImage,
        coverRemoved,
        activeLanguages,
        translations,
        statusByLanguage,
        color,
        order,
      });

      if (!result.ok) {
        if (result.code === 'database') {
          showError(t('exhibitions.databaseError'));
        } else if (result.code === 'validation') {
          result.errors.forEach((error) => showError(error));
        }
        return;
      }

      if (result.exhibition) {
        hydrateExhibition(result.exhibition);

        if (result.didUploadImage) {
          setSelectedImage(null);
          if (result.coverUrl) {
            try {
              const { getFirebaseImageUrl } = await import('@/shared/utils/imageUtils');
              const imageUrl = await getFirebaseImageUrl(result.coverUrl);
              setImagePreview(imageUrl);
            } catch (error) {
              logger.error("Erreur lors du rechargement de l'image:", error);
            }
          }
        }
      }

      setIsEditing(false);
    } catch (error) {
      logger.error("Erreur lors de la sauvegarde de l'exposition:", error);
      showError(handleFirebaseError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      setIsEditing(false);
      if (exhibition) {
        hydrateExhibition(exhibition);
        setSelectedImage(null);
        if (exhibition.cover) {
          (async () => {
            try {
              const { getFirebaseImageUrl } = await import('@/shared/utils/imageUtils');
              const imageUrl = await getFirebaseImageUrl(exhibition.cover);
              setImagePreview(imageUrl);
            } catch (error) {
              logger.error("Erreur lors du rechargement de l'image:", error);
            }
          })();
        } else {
          setImagePreview(null);
        }
      }
    } else {
      navigate(-1);
    }
  };

  const handleWorkCreated = async () => {
    await loadExhibition();
    await refetchWorks();
  };

  const handleEditWork = (work: Work) => {
    setEditingWork(work);
    setCreateWorkModalOpen(true);
  };

  const handleCloseWorkModal = () => {
    setCreateWorkModalOpen(false);
    setEditingWork(null);
  };

  const handleDragStart = (workId: string) => {
    setDraggedWorkId(workId);
  };

  const handleDragOver = (e: React.DragEvent, workId: string, place: 'before' | 'after') => {
    e.preventDefault();
    if (draggedWorkId && draggedWorkId !== workId) {
      setDragOverWorkId(workId);
      setDragOverPlace(place);
    }
  };

  const handleDragLeave = () => {
    setDragOverWorkId(null);
  };

  const handleDrop = async (e: React.DragEvent, targetWorkId: string, place: 'before' | 'after') => {
    e.preventDefault();
    if (!draggedWorkId || draggedWorkId === targetWorkId || !museum?.id || !exhibitionId) {
      setDraggedWorkId(null);
      setDragOverWorkId(null);
      return;
    }

    const newOrders = reorganizeAfterDrag(works, draggedWorkId, targetWorkId, place === 'after');
    const reordered = [...works]
      .sort((a, b) => (newOrders.get(a.id) ?? 0) - (newOrders.get(b.id) ?? 0))
      .map((item, index) => ({ ...item, order: index + 1 }));
    setWorks(reordered);

    setDraggedWorkId(null);
    setDragOverWorkId(null);

    const success = await reorderWorks(works, draggedWorkId, targetWorkId, place === 'after', {
      skipSuccessCallback: true,
    });
    if (!success) {
      refetchWorks();
    }
  };

  const handleDragEnd = () => {
    setDraggedWorkId(null);
    setDragOverWorkId(null);
  };

  const handleConfirmDelete = async () => {
    if (!exhibitionId) {
      showError(t('exhibitions.exhibitionNotFound'));
      return;
    }

    setDeleting(true);
    try {
      const success = await deleteExhibition(exhibitionId);
      if (success) {
        const slug = museumSlugParam || museumSlug;
        if (slug) {
          navigate(`/${slug}`, { replace: true });
        } else {
          navigate('../', { replace: true });
        }
      } else {
        showError(t('exhibitions.saveError'));
      }
    } catch (error) {
      logger.error("Erreur lors de la suppression de l'exposition:", error);
      showError(handleFirebaseError(error));
    } finally {
      setDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const filteredWorks = works.filter((work) => {
    if (episodeFilter === 'all') return true;
    const currentStatus = work.status?.[currentLanguage] || 'draft';
    if (episodeFilter === 'published') return currentStatus === 'published';
    if (episodeFilter === 'drafts') return currentStatus === 'draft';
    return true;
  });

  const retryLoad = () => {
    setLoading(true);
    loadExhibition();
  };

  return {
    museum,
    museumLoading,
    exhibitionId,
    exhibition,
    loading,
    loadError,
    saving,
    isEditing,
    setIsEditing,
    episodeFilter,
    setEpisodeFilter,
    statusByLanguage,
    setStatusByLanguage,
    color,
    setColor,
    order,
    setOrder,
    selectedImage,
    imagePreview,
    statusDropdownOpen,
    setStatusDropdownOpen,
    colorDropdownOpen,
    setColorDropdownOpen,
    orderDropdownOpen,
    setOrderDropdownOpen,
    languageModalOpen,
    setLanguageModalOpen,
    selectedLanguage,
    setSelectedLanguage,
    languageDropdownOpen,
    setLanguageDropdownOpen,
    createWorkModalOpen,
    setCreateWorkModalOpen,
    editingWork,
    draggedWorkId,
    dragOverWorkId,
    dragOverPlace,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleting,
    activeLanguages,
    setActiveLanguages,
    currentLanguage,
    setCurrentLanguage,
    translations,
    setTranslations,
    museumLanguages,
    effectiveAvailableColors,
    exhibitionColorMap,
    allExhibitions,
    loadingWorks,
    worksError,
    refetchWorks,
    works,
    filteredWorks,
    changeExhibitionOrder,
    navigate,
    handleImageSelect,
    handleImageDelete,
    handleSaveDraft,
    handleCancel,
    handleWorkCreated,
    handleEditWork,
    handleCloseWorkModal,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    handleConfirmDelete,
    retryLoad,
  };
};
